import {
  adobeIdStore,
  basePropertyStore,
  customPropertyStore,
  externalCampaignStore,
  pageInfoState,
  previousEventStore,
} from 'apolloGraphql/mml-apollo-cache';
import { logger } from 'logger';
import type { CustomPropertyType } from 'state-machines/analytic-states';
import { updateVideoSession, VideoSessionName } from 'state-machines/new-video-session-state';
import { getDevice } from './getDevice';
import { datadogRum } from '@datadog/browser-rum';
import { APP_VERSION } from 'version';
import { getDomainEnv } from './assets';
import monitor from './monitor';
import getErrorMessage from './error';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LOG = logger.getLogger('AnalyticsUtil');

const device = getDevice();

class AnalyticsUtil {
  private _previousEvent: string | null = null;
  private _adobeId = '';
  private _refreshAdobeId = false;

  constructor() {
    (async () => {
      if (window._satellite) {
        await this.getAEPIdentity();
      }
    })();
  }

  private async _cacheAdobeId() {
    if (window._satellite && (this._adobeId === '' || this._refreshAdobeId)) {
      await this.getAEPIdentity();
    }
  }

  refreshAdobeId() {
    this._refreshAdobeId = true;
  }

  waitForAlloy(): Promise<void> {
    return new Promise((resolve) => {
      const checkForAlloy = () => {
        if (typeof window.alloy === 'function') {
          resolve();
          return;
        }

        window.setTimeout(checkForAlloy, 500);
      };

      checkForAlloy();
    });
  }

  async getAEPIdentity(): Promise<void> {
    this._refreshAdobeId = false;
    try {
      if (typeof window.alloy !== 'function') {
        await this.waitForAlloy();
      }
      const result = await window.alloy('getIdentity');
      this._adobeId = result.identity.ECID ?? '';
      adobeIdStore(this._adobeId);
    } catch (e) {
      const error = getErrorMessage(e);
      LOG.error('Error getting AEP ECID:', error);
      monitor.sendError('Error getting AEP ECID', { error });
    }
  }

  private _commonProperties() {
    return {
      visitorId: this._adobeId,
      anonymousId: this._adobeId,
      platform: device,
      appVersion: APP_VERSION.version,
      environment: getDomainEnv(),
    };
  }

  /**
   * Manually track analytic events while setting customPropertyStore
   * without relying on React lifecycle to prevent losing events between renders
   */
  async trackEvent(customPropertyInfo: CustomPropertyType, sessionName?: VideoSessionName) {
    if (customPropertyInfo.eventName !== '') {
      const { eventName, ...customAttributes } = customPropertyInfo;

      if (typeof customPropertyInfo.videoAttribution === 'string') {
        updateVideoSession(
          { videoProperties: { videoAttribution: customPropertyInfo.videoAttribution } },
          true,
          sessionName,
        );
      }

      previousEventStore(this._previousEvent + '');
      await this._cacheAdobeId();

      const aepProperties = {
        eventType: 'interaction',
        ...this._commonProperties(),
        ...customAttributes,
      };

      window.mmlTrack?.('mml_interaction', {
        __adobe: {
          analytics: {
            linkName: eventName,
            linkType: 'o',
            contextData: {
              ...aepProperties,
            },
          },
        },
      });
      datadogRum.addAction(eventName, aepProperties);

      externalCampaignStore('');
      customPropertyStore(customPropertyInfo);
      this._previousEvent = eventName;
    }
  }

  async page(params: { name: string; properties: Record<string, unknown> }) {
    await this._cacheAdobeId();
    const baseProperties = basePropertyStore();

    const aepProperties = {
      eventType: 'screen',
      ...this._commonProperties(),
      ...baseProperties,
      ...params.properties,
    };

    window.mmlTrack?.('mml_screen', {
      __adobe: {
        analytics: {
          pageName: params.name,
          contextData: {
            ...aepProperties,
          },
        },
      },
    });

    if (typeof params.properties.ddName === 'string' && typeof params.properties.path === 'string') {
      // Page tracking for DD is handled by starting a new screen view
      datadogRum.startView(params.properties.ddName);
      pageInfoState({
        referrer: pageInfoState().path,
        path: params.properties.path,
      });
    }
  }

  async identify(properties: Record<string, unknown>) {
    await this._cacheAdobeId();

    const aepProperties = {
      eventType: 'identify',
      ...this._commonProperties(),
      ...properties,
    };

    window.mmlTrack?.('mml_interaction', {
      __adobe: {
        analytics: {
          linkName: 'identify',
          linkType: 'o',
          contextData: {
            ...aepProperties,
          },
        },
      },
    });
  }

  async lifecycle(params: { name: string; properties: Record<string, unknown> }) {
    await this._cacheAdobeId();
    const baseProperties = basePropertyStore();

    const aepProperties = {
      eventType: 'lifecycle',
      lifecycleEvent: params.name,
      ...this._commonProperties(),
      ...baseProperties,
      ...params.properties,
    };

    window.mmlTrack?.('mml_interaction', {
      __adobe: {
        analytics: {
          linkName: params.name,
          linkType: 'o',
          contextData: {
            ...aepProperties,
          },
        },
      },
    });
  }
}

const analyticsUtil = new AnalyticsUtil();

export { analyticsUtil as AnalyticsUtil };

export const IS_CONVIVA_ENABLED = false;
